import rawCalibration from './calibration.generated.json';
import type { Difficulty } from './types';

export interface OpponentEloRating {
  rating: number;
  ciLow: number;
  ciHigh: number;
  games: number;
  wins: number;
  draws: number;
  losses: number;
}

export interface EloCalibration {
  schemaVersion: number;
  generatedAt: string;
  status: 'calibrated' | 'provisional';
  engineGeneration?: string;
  methodology: string;
  poolAnchor: string;
  totalGames: number;
  gamesPerPair: number;
  adjacentGames?: number;
  bootstrapSamples: number;
  maxCiHalfWidth: number;
  decisiveRate: number;
  monotonic?: boolean;
  minimumAdjacentGap?: number;
  ratings: Record<Difficulty, OpponentEloRating>;
}

export const ELO_CALIBRATION = rawCalibration as unknown as EloCalibration;
export const CURRENT_ENGINE_GENERATION = 'v5.0-twelve-guardians';
export const IS_CURRENT_ELO = ELO_CALIBRATION.status === 'calibrated'
  && ELO_CALIBRATION.engineGeneration === CURRENT_ENGINE_GENERATION;

export function hasOpponentElo(id: Difficulty): boolean {
  const rating = (ELO_CALIBRATION.ratings as Partial<Record<Difficulty, OpponentEloRating>>)[id];
  return Boolean(rating
    && Number.isFinite(rating.rating)
    && Number.isFinite(rating.ciLow)
    && Number.isFinite(rating.ciHigh)
    && Number.isFinite(rating.games));
}

export function isCompleteCurrentElo(ids: readonly Difficulty[]): boolean {
  return IS_CURRENT_ELO && ids.every(hasOpponentElo);
}

export function opponentElo(id: Difficulty): OpponentEloRating {
  return ELO_CALIBRATION.ratings[id];
}

export function eloLabel(id: Difficulty): string {
  return IS_CURRENT_ELO && hasOpponentElo(id) ? `站內 Elo ${opponentElo(id).rating}` : '新版 Elo 校準中';
}

export function eloIntervalLabel(id: Difficulty): string {
  const rating = opponentElo(id);
  return `95% 信賴區間 ${rating.ciLow}–${rating.ciHigh}`;
}
